import React, { useState } from "react";
import { Link } from "react-router-dom";
import { VENUE_INFO, mapsUrl } from "../utils/venueInfo";

// Per-venue editorial copy. The factual shopfront details (name, address, phone,
// website) come from VENUE_INFO so this page can't drift from the roster's info box
// and the profile dialog's call/message buttons.
const VENUE_BLURBS = [
  {
    slug: "marrickville",
    heading: "No 5 Marrickville",
    area: "Marrickville, Inner West Sydney",
    cadence: "Daily (today & tomorrow)",
    blurb:
      "Complete daily roster for the Marrickville location. View current and next day rosters, " +
      "with shift times, names, and codes.",
  },
  {
    slug: "cleveland",
    heading: "Ginza Cleveland St",
    area: "Cleveland Street, Surry Hills",
    cadence: "Today & tomorrow, refreshed through the day",
    blurb:
      "Cleveland Street roster information updated throughout the day. Search for specific names " +
      "or shift times, and open a profile for stats and photos.",
  },
  {
    slug: "elizabeth",
    heading: "479 Ginza Elizabeth St",
    area: "Elizabeth Street, Surry Hills",
    cadence: "Today & tomorrow, refreshed through the day",
    blurb:
      "Elizabeth Street roster tracking with instant access to today's and tomorrow's shifts, " +
      "plus full profiles where the venue publishes them.",
  },
  {
    slug: "gladesville",
    heading: "The Nightshade Gladesville",
    area: "Gladesville, Sydney",
    cadence: "Daily (today & tomorrow)",
    blurb:
      "Gladesville roster updates with current and next-day information, tracked automatically " +
      "so you don't have to keep checking.",
  },
];

function About() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (faqNumber) => {
    setOpenFaq(openFaq === faqNumber ? null : faqNumber);
  };

  return (
    <div className="about-page">
      <div className="mb-4">
        <Link to="/" className="btn btn-outline-primary btn-sm">
          ← Back to Rosters
        </Link>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h1 className="h3 text-primary mb-4">About Sydney Roster Scheduler</h1>

          <p className="lead">
            Sydney Roster Scheduler is Sydney's premier roster tracking platform, providing real-time 
            work schedule information for multiple venues across the city. We aggregate roster data 
            from official venue sources and present it in an easy-to-use, searchable format — and we 
            can email you when someone you follow is next on the roster.
          </p>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Our Mission</h2>
          <p>
            We created Sydney Roster Scheduler to solve a simple problem: checking work rosters 
            shouldn't be complicated. Instead of visiting multiple venue websites or checking different 
            sources, our platform brings all Sydney roster information together in one convenient location.
          </p>
          <p>
            Whether you're checking the <strong>No 5 Marrickville roster</strong>, looking up shifts at 
            <strong> Ginza Cleveland St</strong>, browsing the <strong>479 Ginza Elizabeth St schedule</strong>, 
            or viewing <strong>The Nightshade Gladesville roster</strong>, we've got you covered.
          </p>

          <hr className="my-4" />

          {/* ---------------------------------------------------------------- */}
          {/* Notifications                                                     */}
          {/* ---------------------------------------------------------------- */}
          <h2 className="h4 text-primary mb-3">🔔 Get Notified When Someone's Back</h2>

          <p>
            Rather than checking every day, you can follow anyone on the roster and we'll let you know 
            when they're next working. Tap the <strong>bell</strong> next to a name — or the 
            <strong> Notify me</strong> button in their profile — and you're following them.
          </p>

          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <h3 className="h6 fw-bold">How it works</h3>
                  <ol className="small mb-0 ps-3">
                    <li>Tap the bell beside any name on the roster.</li>
                    <li>Sign in with your email address — we send a one-time link, so there's no password to remember.</li>
                    <li>
                      We email you at around <strong>7:30pm</strong> when someone you follow appears on 
                      tomorrow's roster.
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <h3 className="h6 fw-bold">Good to know</h3>
                  <ul className="small mb-0 ps-3">
                    <li>
                      <strong>One email a night</strong>, listing everyone you follow — not one message per person.
                    </li>
                    <li>
                      Optional <strong>Telegram</strong> alerts if you'd rather not use email.
                    </li>
                    <li>
                      Unfollow anytime from the{" "}
                      <Link to="/notifications">Notifications page</Link>, even if that person isn't 
                      currently rostered.
                    </li>
                    <li>We only use your email address to send the alerts you asked for.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-2">
            <Link to="/notifications" className="btn btn-primary">
              Set up notifications →
            </Link>
          </div>

          <hr className="my-4" />

          {/* ---------------------------------------------------------------- */}
          {/* Venues, with real shopfront details                               */}
          {/* ---------------------------------------------------------------- */}
          <h2 className="h4 text-primary mb-3">Venues We Track</h2>

          <p className="text-muted small">
            Contact details below are published by each venue on their own website. They can change — 
            please confirm with the venue before travelling.
          </p>

          <div className="row">
            {VENUE_BLURBS.map((v) => {
              const info = VENUE_INFO[v.slug] || {};
              return (
                <div className="col-md-6 mb-4" key={v.slug}>
                  <div className="card h-100 border-primary">
                    <div className="card-header bg-primary text-white">
                      <h3 className="h6 mb-0">🏢 {v.heading}</h3>
                    </div>
                    <div className="card-body">
                      {info.name && (
                        <p className="mb-2">
                          <strong>Trading as:</strong> {info.name}
                        </p>
                      )}
                      <p className="mb-2">
                        <strong>Area:</strong> {v.area}
                      </p>
                      {info.address && (
                        <p className="mb-2">
                          <strong>Address:</strong>{" "}
                          <a
                            href={mapsUrl(info.address)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {info.address}
                          </a>
                          {info.addressNote && (
                            <span className="d-block text-muted small">{info.addressNote}</span>
                          )}
                        </p>
                      )}
                      {info.phone && (
                        <p className="mb-2">
                          <strong>Phone:</strong>{" "}
                          <a href={`tel:${info.phoneDial}`}>{info.phone}</a>
                        </p>
                      )}
                      {info.website && (
                        <p className="mb-2">
                          <strong>Website:</strong>{" "}
                          <a href={info.website} target="_blank" rel="noopener noreferrer">
                            {info.website.replace(/^https?:\/\//, "")}
                          </a>
                        </p>
                      )}
                      {info.hours && (
                        <p className="mb-2">
                          <strong>Hours:</strong> {info.hours}
                        </p>
                      )}
                      <p className="mb-2">
                        <strong>Roster updates:</strong> {v.cadence}
                      </p>
                      <p className="mb-0">{v.blurb}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Platform Features</h2>

          <div className="row">
            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🔔</div>
                <h3 className="h6 fw-bold">Availability Alerts</h3>
                <p className="text-muted small">
                  Follow anyone on the roster and get one email a night when they're on tomorrow's list.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🔄</div>
                <h3 className="h6 fw-bold">Automatic Updates</h3>
                <p className="text-muted small">
                  Rosters are automatically refreshed through the day, so you always have current information.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">📅</div>
                <h3 className="h6 fw-bold">Today & Tomorrow</h3>
                <p className="text-muted small">
                  View both current day and next day rosters for better planning and schedule awareness.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🔍</div>
                <h3 className="h6 fw-bold">Search & Filter</h3>
                <p className="text-muted small">
                  Quickly find specific names, codes, or shift times with our powerful search functionality.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🖼️</div>
                <h3 className="h6 fw-bold">List or Gallery</h3>
                <p className="text-muted small">
                  Switch between a sortable table and a photo gallery — your choice is remembered.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">👤</div>
                <h3 className="h6 fw-bold">Performer Profiles</h3>
                <p className="text-muted small">
                  Open any name for stats, a description, a photo gallery, and one-tap call or message.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">📞</div>
                <h3 className="h6 fw-bold">Venue Contact</h3>
                <p className="text-muted small">
                  Each venue's address, map link, and phone number are one tap away from the roster.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">📱</div>
                <h3 className="h6 fw-bold">Mobile Optimized</h3>
                <p className="text-muted small">
                  Access rosters on any device - desktop, tablet, or smartphone with responsive design.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🆓</div>
                <h3 className="h6 fw-bold">Free to Use</h3>
                <p className="text-muted small">
                  No payments, ever. Browsing needs no account — you only sign in if you want alerts.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">How It Works</h2>

          <ol className="list-group list-group-numbered mb-4">
            <li className="list-group-item">
              <strong>Automated Data Collection:</strong> Our system automatically checks each venue's 
              official roster source several times a day.
            </li>
            <li className="list-group-item">
              <strong>Data Processing:</strong> Roster information is extracted, cleaned, and formatted 
              for easy reading.
            </li>
            <li className="list-group-item">
              <strong>Real-Time Updates:</strong> New roster data is made available on our platform as 
              soon as it's published by the venues.
            </li>
            <li className="list-group-item">
              <strong>Availability Alerts:</strong> If you've followed anyone, we check the next day's 
              roster each evening and email you a single summary.
            </li>
            <li className="list-group-item">
              <strong>User Access:</strong> You visit our site and instantly see the latest rosters for 
              all tracked venues in one place.
            </li>
          </ol>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Why Use Sydney Roster Scheduler?</h2>

          <div className="alert alert-primary">
            <h3 className="h6 fw-bold mb-2">✨ All-in-One Platform</h3>
            <p className="mb-0">
              Instead of bookmarking and checking multiple venue websites, access all Sydney roster 
              information from a single, convenient location. Save time and stay organized.
            </p>
          </div>

          <div className="alert alert-success">
            <h3 className="h6 fw-bold mb-2">⚡ Always Up-to-Date</h3>
            <p className="mb-0">
              Never miss a roster update. Our automated system ensures you're always seeing the most 
              current schedule information available from each venue.
            </p>
          </div>

          <div className="alert alert-warning">
            <h3 className="h6 fw-bold mb-2">🔔 Stop Checking, Start Following</h3>
            <p className="mb-0">
              Follow the people you're interested in and let the site tell you when they're next on. 
              One email a night, and you can unfollow at any time.
            </p>
          </div>

          <div className="alert alert-info">
            <h3 className="h6 fw-bold mb-2">🎯 Easy to Use</h3>
            <p className="mb-0">
              Clean, intuitive interface designed for quick roster checks. Find what you need in seconds 
              with search, filter, and sort capabilities.
            </p>
          </div>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Frequently Asked Questions</h2>

          <div className="accordion mb-4" id="faqAccordion">
            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${openFaq !== 1 ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleFaq(1)}
                >
                  How often are rosters updated?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 1 ? 'show' : ''}`}>
                <div className="accordion-body">
                  Rosters are automatically updated several times throughout the day. Most venues publish 
                  the next day's roster in the evening, so tomorrow's list usually appears after about 7pm.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${openFaq !== 2 ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleFaq(2)}
                >
                  Do I need to create an account?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 2 ? 'show' : ''}`}>
                <div className="accordion-body">
                  Not for browsing — all roster information is free and open, with no sign-up. You only 
                  need to sign in if you want availability alerts, and even then there's no password: we 
                  email you a one-time sign-in link.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${openFaq !== 3 ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleFaq(3)}
                >
                  How do notifications work, and how do I stop them?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 3 ? 'show' : ''}`}>
                <div className="accordion-body">
                  Tap the bell next to a name to follow them. Each evening around 7:30pm we check the next 
                  day's rosters and send you a single email listing everyone you follow who's on. To stop, 
                  open the <Link to="/notifications">Notifications page</Link> and remove them — that works 
                  whether or not they're currently rostered. You can also link Telegram if you'd prefer 
                  messages there, and send the bot <code>/stop</code> to unlink.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${openFaq !== 4 ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleFaq(4)}
                >
                  Is the roster information accurate?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 4 ? 'show' : ''}`}>
                <div className="accordion-body">
                  We pull roster data directly from official venue sources. However, we always recommend 
                  confirming important details — including addresses, phone numbers, and pricing — with the 
                  venue directly, as these can change.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${openFaq !== 5 ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleFaq(5)}
                >
                  Can I see photos and details for a specific performer?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 5 ? 'show' : ''}`}>
                <div className="accordion-body">
                  Yes. Open any name for their profile, which includes stats, a description, and a photo 
                  gallery when available, plus buttons to call or message the venue. You can also switch 
                  the whole roster to gallery view to browse photos side by side.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${openFaq !== 6 ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleFaq(6)}
                >
                  Can you add more venues?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 6 ? 'show' : ''}`}>
                <div className="accordion-body">
                  We're always looking to expand! If you'd like to suggest a venue for roster tracking, 
                  please contact us through our support channels.
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="text-center">
            <h2 className="h4 text-primary mb-3">Ready to Check Rosters?</h2>
            <p className="mb-4">
              Access Sydney roster schedules for No 5 Marrickville, Ginza Cleveland St, 479 Ginza Elizabeth St, 
              and The Nightshade Gladesville - all in one convenient location.
            </p>
            <Link to="/" className="btn btn-primary btn-lg">
              View Rosters Now →
            </Link>
          </div>

          <hr className="my-4" />

          <div className="text-muted small">
            <p className="mb-2">
              <strong>Related Searches:</strong> Sydney roster, Marrickville roster schedule, Cleveland Street 
              venue roster, Elizabeth Street roster tracking, Gladesville schedule, aus99 roster, aus99 forum, 
              Sydney nightlife rosters, venue work schedules Sydney, Marrickville work schedule, Sydney roster 
              aggregator, real-time roster updates Sydney, roster availability alerts
            </p>
            <p className="mb-0">
              <strong>Service Area:</strong> Sydney, NSW - Marrickville, Surry Hills, Cleveland Street, 
              Elizabeth Street, Gladesville, Inner West Sydney, Sydney CBD
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
